import { createHtmlElementFromString } from './html.utils';

/**
 * Template for pages that are used in complex components only.
 */
export abstract class PageModule {
  /**
   * The template of the page that is rendered.
   */
  abstract template: string;

  /**
   * Array of selectors that are rendered in this view.
   */
  abstract componentSelector: string;

  /**
   * Component of the page that is generated from the template
   */
  protected pageComponent: HTMLElement;

  /**
   * The translations for the page.
   */
  protected translations: { [key: string]: string } = null;

  /**
   * Renders the content to the given root element.
   * @param parent The root element to append the view to
   * @param args More arguments that need to be passed to render the view
   */
  abstract renderView(parent?: HTMLElement, args?: unknown): void;

  /**
   * Checks if the view already exists in DOM.
   */
  protected isAlreadyRendered(): boolean {
    return document.querySelector(this.componentSelector) !== null;
  }

  /**
   * Renders the page only if it does not exist yet.
   * @param parent The root element to append the view to
   * @param args More arguments that need to be passed to render the view
   * @param showFooter Whether the footer should be hidden or not, this is false by default
   * @param hideFooterOnMobile If the footer should be hidden on mobile (because there is a fixed positioned button row)
   * this only applies when the showFooter property is true.
   */
  public render(parent: HTMLElement, args?: unknown, showFooter?: boolean, hideFooterOnMobile?: boolean): void {
    if (!this.isAlreadyRendered()) {
      // Create element from template
      this.pageComponent = createHtmlElementFromString(this.template);
      // Add element to the e-catalog
      parent.appendChild(this.pageComponent);
      // Render view details
      this.renderView(parent, args);
      // Hide footer for this view
      const footer = document.querySelector<HTMLElement>('footer.footer');
      if (footer) {
        // show or hide the footer
        if (!showFooter) {
          footer.style.display = 'none';
        } else {
          footer.removeAttribute('style');
        }
        if (hideFooterOnMobile) {
          // show the footer but hide it on mobile devices
          footer.classList.add('footer--mobile-hidden');
        } else {
          footer.classList.remove('footer--mobile-hidden');
        }
      }
    }
  }

  /**
   * Removes the view of the page that is rendered in the renderView method.
   */
  public reset(): void {
    const element = document.querySelector(this.componentSelector);
    element?.remove();
  }

  public hide(): void {
    const element: HTMLElement = document.querySelector(this.componentSelector);
    if (element) {
      window.localStorage.setItem('scrollPosition', window.scrollY);
      element.style.display = 'none';
    }
  }

  public show(): void {
    const element: HTMLElement = document.querySelector(this.componentSelector);
    if (element) {
      element.style.display = 'block';
    }
  }
}
